import React from 'react';
import { groupBy, find, merge } from 'lodash';
import { Icon } from 'ui';
import FieldsDropdown from './FieldsDropdown';
import OperatorsDropdown from './OperatorsDropdown';
import GroupsDropdown from './GroupsDropdown';
import ValueOptionsDropdown from './ValueOptionsDropdown';
import RuleDivider from './RuleDivider';
import RuleBuilderDatePicker from './RuleBuilderDatePicker';
import DateWithinFields from './DateWithinFields';

class Rule extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      field: null, // all the information about the first field
      operatorData: null, // dropdown items {array of objects}
      showInput: false,
    };
    this.useData = this.useData.bind(this);
    this.setOperator = this.setOperator.bind(this);
    this.setInputType = this.setInputType.bind(this);
    this.deleteRule = this.deleteRule.bind(this);
    this.validateRule = this.validateRule.bind(this);
    this.updateRuleState = this.updateRuleState.bind(this);
  }

  useData() {
    const { rule, builderRules } = this.props;
    const field = find(builderRules.fields, { name: rule.name });
    const operatorData = builderRules.operators[field.dataType];
    const showInput =
      (rule.value !== null || rule.operator.match(/not|within|between/)) &&
      rule.name !== 'user_role' &&
      rule.name !== 'user_locale';

    this.setState({ field, operatorData, showInput }, () =>
      this.updateRuleState(this.props.rule),
    );
  }

  deleteRule() {
    this.props.removeRule(this.props.ruleIndex, this.props.ruleSetIndex);
  }

  /**
   * Sets the operator given the Rule's name
   *
   * When the field provided from SmartGroups::FieldCollection utilizes value_options,
   * then the operator calculation below is always set to `equalTo`.
   * @see app/models/smart_groups/field_collection.rb
   *
   * @param      {<String}  name               The rule's name (i.e. 'user_role')
   * @param      {<Integer>}  relatedObjectId  The related object's identifier
   */
  setOperator(name, relatedObjectId) {
    const { builderRules } = this.props;
    const field = find(builderRules.fields, { name, relatedObjectId });
    const operatorData = builderRules.operators[field.dataType];
    const operator = field.valueOptions ? 'equalTo' : null;
    const updateData = {
      name,
      relatedObjectId,
      operator,
      value: null,
      startsAt: null,
      endsAt: null,
      unitNumber: null,
      unit: null,
      recalculationErrors: null,
    };

    this.setState({ field, operatorData, showInput: false });

    if (relatedObjectId) {
      updateData.relatedObjectId = relatedObjectId;
    }

    this.updateRuleState(updateData);
  }

  setInputType(operator) {
    const showInput =
      operator !== 'isEmpty' &&
      operator !== 'isNotEmpty' &&
      !this.state.field.valueOptions; // eslint-disable-line react/no-access-state-in-setstate
    const updateData = {
      operator,
      startsAt: null,
      endsAt: null,
    };
    const withinRule = ['withinNext', 'withinLast', 'notWithinLast'].includes(
      operator,
    );

    if (!showInput || withinRule || operator === 'between') {
      updateData.value = null;
    }

    if (!withinRule) {
      updateData.unitNumber = null;
      updateData.unit = null;
    }

    this.setState({ showInput });

    this.updateRuleState(updateData);
  }

  validateRule(rule) {
    const ruleWithChanges = merge(this.props.rule, rule);

    if (ruleWithChanges.recalculationErrors) {
      return false;
    }

    if (
      ['withinNext', 'withinLast', 'notWithinLast'].includes(
        ruleWithChanges.operator,
      )
    ) {
      return !!ruleWithChanges.unit && !!ruleWithChanges.unitNumber;
    }

    if (['isEmpty', 'isNotEmpty'].includes(ruleWithChanges.operator)) {
      return true;
    }

    if (ruleWithChanges.operator === 'between') {
      return !!ruleWithChanges.startsAt && !!ruleWithChanges.endsAt;
    }

    if (['group_manager', 'group_membership'].includes(ruleWithChanges.name)) {
      return !!find(this.props.customGroups, ['id', ruleWithChanges.value]);
    }

    if (ruleWithChanges.value !== null) {
      if (['date', 'time'].includes(this.state.field.dataType)) {
        return moment.utc(ruleWithChanges.value).isValid();
      }

      if (ruleWithChanges.name === 'custom_field') {
        return !!ruleWithChanges.relatedObjectId;
      }

      return ruleWithChanges.value.trim().length > 0;
    }

    return false;
  }

  updateRuleState(mergeObject) {
    const { ruleIndex, ruleSetIndex, updateRule } = this.props;

    mergeObject.valid = this.validateRule(mergeObject);
    updateRule(ruleIndex, ruleSetIndex, mergeObject);
  }

  componentDidMount() {
    if (this.props.rule.name) {
      this.useData();
    }
  }

  get groupedFields() {
    const { builderRules, rule } = this.props;
    const { fields, hiddenUserFields } = builderRules;

    const filteredFields = fields.filter(field => {
      if (field.category !== 'Users') return true;

      const isVisibleOrCurrentRule =
        !hiddenUserFields.includes(field.name) ||
        (!!rule && field.name === rule.name);

      return isVisibleOrCurrentRule;
    });

    return groupBy(filteredFields, 'category');
  }

  render() {
    const {
      rule,
      last,
      addNewRule,
      ruleSetIndex,
      ruleIndex,
      lastRuleSet,
      builderRules,
      customGroups,
    } = this.props;
    const hideDeleteButton =
      ruleIndex === 0 && ruleSetIndex === 0 && last && lastRuleSet;
    const showDateWithin = [
      'withinNext',
      'withinLast',
      'notWithinLast',
    ].includes(rule.operator);

    return (
      <div>
        <div className="Rule d-f ai-c jc-sb">
          <div>
            <FieldsDropdown
              name={FieldsDropdown.optionValue(rule)}
              groupedFields={this.groupedFields}
              setOperator={this.setOperator}
            />
            {this.state.operatorData && (
              <span>
                {!this.state.field.valueOptions && (
                  <OperatorsDropdown
                    data={this.state.operatorData}
                    field={this.state.field}
                    operator={rule.operator}
                    setInputType={this.setInputType}
                  />
                )}
                {this.state.field.valueOptions && (
                  <ValueOptionsDropdown
                    field={this.state.field}
                    valueOptions={this.state.field.valueOptions}
                    onChange={this.updateRuleState}
                    value={rule.value}
                  />
                )}
              </span>
            )}
            {this.state.showInput && (
              <span>
                {this.state.field.dataType === 'string' && (
                  <input
                    value={rule.value || ''}
                    className="Rule-input NewInput NewInput--white"
                    onChange={e =>
                      this.updateRuleState({ value: e.target.value })
                    }
                    aria-label={t(
                      'smart_groups.create_modal.rule_criteria_input',
                    )}
                  />
                )}
                {['group_manager', 'group_membership'].includes(
                  this.state.field.name,
                ) && (
                  <GroupsDropdown
                    groups={customGroups}
                    onChange={this.updateRuleState}
                    value={rule.value}
                  />
                )}
                {['date', 'time'].includes(this.state.field.dataType) &&
                  !showDateWithin && (
                    <span>
                      {rule.operator !== 'between' && (
                        <RuleBuilderDatePicker
                          stateKey="value"
                          selected={
                            moment.utc(rule.value).isValid()
                              ? moment.utc(rule.value)
                              : ''
                          }
                          onChange={this.updateRuleState}
                        />
                      )}
                      {rule.operator === 'between' && (
                        <span>
                          <RuleBuilderDatePicker
                            stateKey="startsAt"
                            selected={
                              moment.utc(rule.startsAt).isValid()
                                ? moment.utc(rule.startsAt)
                                : ''
                            }
                            onChange={this.updateRuleState}
                          />
                          <span className="mr-8 ml-8 fz-bigger">and</span>
                          <RuleBuilderDatePicker
                            stateKey="endsAt"
                            selected={
                              moment.utc(rule.endsAt).isValid()
                                ? moment.utc(rule.endsAt)
                                : ''
                            }
                            onChange={this.updateRuleState}
                          />
                        </span>
                      )}
                    </span>
                  )}
                {showDateWithin && (
                  <DateWithinFields
                    units={builderRules.units}
                    unit={rule.unit}
                    unitNumber={rule.unitNumber}
                    onChange={this.updateRuleState}
                  />
                )}
              </span>
            )}
          </div>
          {!hideDeleteButton && (
            <Icon
              name="error"
              className="Rule-deleteBtn"
              onClick={this.deleteRule}
              tabIndex="0"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  this.deleteRule();
                }
              }}
            />
          )}
        </div>
        {rule.recalculationErrors &&
          rule.recalculationErrors.map((error, errorIndex) => (
            <span
              className="ErrorContainer ErrorContainer--brandRed Animated--fadeOut d-ib ml-16"
              key={rule.id}
            >
              <Icon name="warning_sign" className="mr-8" />
              {error}
            </span>
          ))}
        <RuleDivider
          last={last}
          addNewRule={addNewRule}
          ruleSetIndex={ruleSetIndex}
        />
      </div>
    );
  }
}

export default Rule;
